import type { GeolocationSignals } from '@fraudeai/schema';
import { defineModule, type ModuleContext } from '../harness.js';
import { attemptAsync, round, withTimeout } from '../util/safe.js';

function empty(requested: boolean): GeolocationSignals {
  return {
    supported: 'geolocation' in navigator,
    permissionState: null,
    permissionRequested: requested,
    latitudeRounded: null,
    longitudeRounded: null,
    accuracyMeters: null,
    altitudeAccuracyMeters: null,
    speed: null,
    heading: null,
    ageMs: null,
    errorCode: null,
    errorMessage: null,
  };
}

async function permissionState(): Promise<string | null> {
  const status = await attemptAsync(() =>
    navigator.permissions?.query({ name: 'geolocation' as PermissionName }),
  );
  return status?.state ?? null;
}

function getPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 7000,
    });
  });
}

export const geolocationModule = defineModule({
  id: 'geolocation',
  phase: 1,
  timeoutMs: 9000,
  async run(ctx: ModuleContext): Promise<GeolocationSignals> {
    const requested = ctx.options.requestGeolocationProbe === true;
    const base = empty(requested);
    base.permissionState = await permissionState();

    if (!requested || !navigator.geolocation?.getCurrentPosition) {
      return base;
    }

    const position = await attemptAsync(() => withTimeout(getPosition(), 8000, 'geolocation'));
    if (!position) {
      return {
        ...base,
        errorCode: 0,
        errorMessage: 'position-unavailable',
      };
    }

    const coords = position.coords;
    return {
      ...base,
      latitudeRounded: round(coords.latitude, 3),
      longitudeRounded: round(coords.longitude, 3),
      accuracyMeters: typeof coords.accuracy === 'number' ? round(coords.accuracy, 1) : null,
      altitudeAccuracyMeters:
        typeof coords.altitudeAccuracy === 'number' ? round(coords.altitudeAccuracy, 1) : null,
      speed: typeof coords.speed === 'number' ? round(coords.speed, 2) : null,
      heading: typeof coords.heading === 'number' ? round(coords.heading, 1) : null,
      ageMs: Math.max(0, Math.round(Date.now() - position.timestamp)),
      errorCode: null,
      errorMessage: null,
    };
  },
});
